import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, Output, ViewChild } from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete'
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { xml } from '@codemirror/lang-xml'
import { bracketMatching, defaultHighlightStyle, foldKeymap, indentOnInput, syntaxHighlighting } from '@codemirror/language'
import { lintKeymap } from '@codemirror/lint'
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search'
import { EditorState, Extension } from '@codemirror/state'
import { oneDark } from '@codemirror/theme-one-dark'
import { crosshairCursor, drawSelection, dropCursor, EditorView, highlightActiveLine, highlightSpecialChars, keymap, rectangularSelection } from '@codemirror/view'
import * as beautify from 'js-beautify'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { EmailTheme } from '../../interfaces/email-theme.interface'
import { MjmlService } from '../../services/mjml.service'

@Component({
  selector: 'ngx-mjml-editor',
  templateUrl: './mjml-editor.component.html',
  styleUrls: ['./mjml-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MjmlEditorComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class MjmlEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef
  @Input() theme: EmailTheme | null = null
  @Input() availableThemes: EmailTheme[] = []
  @Input() variables: Record<string, any> = {}
  @Input() disabled = false
  @Input() placeholder = '<mj-section>\n  <mj-column>\n    <mj-text>Your content here</mj-text>\n  </mj-column>\n</mj-section>'
  @Output() mjmlChange = new EventEmitter<string>()
  @Output() themeChange = new EventEmitter<EmailTheme>()

  private editor!: EditorView
  private destroy$ = new Subject<void>()
  private mjmlSubject = new Subject<string>()

  value = ''
  renderedHtml: SafeHtml = ''
  previewError: string | null = null
  isLoading = false

  // MJML Snippets
  sectionSnippet = `<mj-section background-color="{{theme.background_color}}">
  <mj-column>
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Your content here
    </mj-text>
  </mj-column>
</mj-section>`

  buttonSnippet = `<mj-button background-color="{{theme.primary_color}}" 
           color="white" 
           border-radius="{{theme.border_radius}}"
           href="#">
  Click Here
</mj-button>`

  textSnippet = `<mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}" font-family="{{theme.font_family}}">
  Your text content here
</mj-text>`

  imageSnippet = `<mj-image width="100px" src="https://placeholdit.com/600x400" alt="Image description"></mj-image>`

  dividerSnippet = `<mj-divider border-color="{{theme.primary_color}}" border-width="2px"></mj-divider>`

  spacerSnippet = `<mj-spacer height="20px"></mj-spacer>`

  heroSnippet = `<mj-hero mode="fluid-height" background-color="{{theme.primary_color}}">
  <mj-text font-size="45px" color="white" font-family="{{theme.font_family}}">
    Hero Title
  </mj-text>
  <mj-text color="white" font-size="18px" font-family="{{theme.font_family}}">
    Hero subtitle
  </mj-text>
  <mj-button background-color="white" color="{{theme.primary_color}}">
    Call to Action
  </mj-button>
</mj-hero>`

  columnsSnippet = `<mj-section>
  <mj-column width="50%">
    <mj-text>Left column content</mj-text>
  </mj-column>
  <mj-column width="50%">
    <mj-text>Right column content</mj-text>
  </mj-column>
</mj-section>`

  // ControlValueAccessor implementation
  private onChange = (value: string) => {}
  private onTouched = () => {}

  constructor(private sanitizer: DomSanitizer, private mjmlService: MjmlService) {
    // Setup debounced MJML rendering
    this.mjmlSubject.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe((mjml) => {
      this.renderMjml(mjml)
    })
  }

  ngAfterViewInit(): void {
    this.initializeEditor()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    if (this.editor) {
      this.editor.destroy()
    }
  }

  private initializeEditor(): void {
    // Create a basic setup equivalent
    const basicExtensions: Extension[] = [
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      bracketMatching(),
      closeBrackets(),
      autocompletion(),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...searchKeymap, ...historyKeymap, ...foldKeymap, ...completionKeymap, ...lintKeymap, indentWithTab])
    ]

    const startState = EditorState.create({
      doc: this.value || this.placeholder,
      extensions: [
        ...basicExtensions,
        xml(),
        oneDark,
        EditorView.lineWrapping, // Enable line wrapping
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const newValue = update.state.doc.toString()
            this.updateValue(newValue)
          }
        }),
        EditorView.theme({
          '&': {
            height: '400px',
            fontSize: '14px'
          },
          '.cm-content': {
            padding: '16px',
            minHeight: '400px'
          },
          '.cm-focused': {
            outline: 'none'
          }
        })
      ]
    })

    this.editor = new EditorView({
      state: startState,
      parent: this.editorContainer.nativeElement
    })

    // Initial render
    if (this.value) {
      this.mjmlSubject.next(this.value)
    }
  }

  private updateValue(newValue: string): void {
    if (this.disabled) {
      return // Don't update if disabled
    }

    this.value = newValue
    this.onChange(newValue)
    this.onTouched()
    this.mjmlChange.emit(newValue)
    this.mjmlSubject.next(newValue)
  }

  private renderMjml(mjml: string): void {
    if (!mjml || mjml.trim() === '') {
      this.renderedHtml = ''
      this.previewError = null
      return
    }

    this.isLoading = true
    this.previewError = null

    try {
      const fullMjml = this.wrapMjmlContent(mjml)
      this.mjmlService
        .renderMjml(fullMjml, this.theme, this.variables)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (html) => {
            this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(html)
            this.previewError = null
            this.isLoading = false
          },
          error: (error) => {
            console.error('MJML rendering error:', error)
            this.previewError = error.message || 'Failed to render MJML'
            this.renderedHtml = ''
            this.isLoading = false
          }
        })
    } catch (error: any) {
      this.previewError = error.message || 'Invalid MJML syntax'
      this.renderedHtml = ''
      this.isLoading = false
    }
  }

  private wrapMjmlContent(mjmlContent: string): string {
    // Add the full MJML structure that's missing from component templates
    const themeStyles = this.getThemeStyles()

    return `
      <mjml>
        <mj-head>
          ${themeStyles}
          <mj-attributes>
            <mj-all font-family="${this.theme?.global_css?.font_family || 'Arial, sans-serif'}" />
            <mj-text font-size="${this.theme?.global_css?.font_size || '16px'}" 
                     line-height="${this.theme?.global_css?.line_height || '1.5'}" 
                     color="${this.theme?.global_css?.text_color || '#000000'}" />
            <mj-section background-color="${this.theme?.global_css?.background_color || '#ffffff'}" 
                        padding="${this.theme?.global_css?.padding || '20px'}" />
          </mj-attributes>
        </mj-head>
        <mj-body background-color="${this.theme?.global_css?.background_color || '#ffffff'}">
          ${mjmlContent}
        </mj-body>
      </mjml>
    `
  }

  private getThemeStyles(): string {
    if (!this.theme?.global_css) return ''

    const css = this.theme.global_css
    const styles = []

    // Add custom CSS styles based on theme
    if (css.primary_color) {
      styles.push(`
        .primary-color { color: ${css.primary_color} !important; }
        .primary-bg { background-color: ${css.primary_color} !important; }
      `)
    }

    if (css.secondary_color) {
      styles.push(`
        .secondary-color { color: ${css.secondary_color} !important; }
        .secondary-bg { background-color: ${css.secondary_color} !important; }
      `)
    }

    if (css.border_radius) {
      styles.push(`
        .rounded { border-radius: ${css.border_radius} !important; }
      `)
    }

    return styles.length > 0 ? `<mj-style>${styles.join('\n')}</mj-style>` : ''
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    this.value = value || ''
    if (this.editor) {
      const transaction = this.editor.state.update({
        changes: {
          from: 0,
          to: this.editor.state.doc.length,
          insert: this.value
        }
      })
      this.editor.dispatch(transaction)
      this.mjmlSubject.next(this.value)
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    // Note: In this implementation, we'll handle disabled state through the component
    // The editor will remain functional but the component can prevent changes
  }

  // Public methods for parent components
  refreshPreview(): void {
    this.mjmlSubject.next(this.value)
  }

  insertMjmlSnippet(snippet: string): void {
    if (this.editor && !this.disabled) {
      const { from } = this.editor.state.selection.main
      const transaction = this.editor.state.update({
        changes: { from, insert: snippet }
      })
      this.editor.dispatch(transaction)
      this.editor.focus()
    }
  }

  // Theme switching
  onThemeSelect(theme: EmailTheme): void {
    this.theme = theme
    this.themeChange.emit(theme)
    this.refreshPreview()
  }

  onThemeSelectChange(event: Event): void {
    const target = event.target as HTMLSelectElement
    const themeId = parseInt(target.value)
    const selectedTheme = this.availableThemes.find((t) => t.id === themeId)
    if (selectedTheme) {
      this.onThemeSelect(selectedTheme)
    }
  }

  // Get theme variables for dropdown
  getThemeVariables(): Array<{ key: string; value: any; display: string }> {
    if (!this.theme?.global_css) return []

    return Object.entries(this.theme.global_css).map(([key, value]) => ({
      key,
      value,
      display: `{{theme.${key}}} - ${value}`
    }))
  }

  // Get component variables for dropdown
  getComponentVariables(): Array<{ key: string; value: any; display: string }> {
    if (!this.variables) return []

    return Object.entries(this.variables).map(([key, value]) => ({
      key,
      value,
      display: `{{component.${key}}} - ${value}`
    }))
  }

  // Insert theme variable
  insertThemeVariable(variableKey: string): void {
    if (this.editor && !this.disabled) {
      const variableText = `{{theme.${variableKey}}}`
      const { from } = this.editor.state.selection.main
      const transaction = this.editor.state.update({
        changes: { from, insert: variableText }
      })
      this.editor.dispatch(transaction)
      this.editor.focus()
    }
  }

  // Insert component variable
  insertComponentVariable(variableKey: string): void {
    if (this.editor && !this.disabled) {
      const variableText = `{{component.${variableKey}}}`
      const { from } = this.editor.state.selection.main
      const transaction = this.editor.state.update({
        changes: { from, insert: variableText }
      })
      this.editor.dispatch(transaction)
      this.editor.focus()
    }
  }

  // Format MJML code using js-beautify
  formatCode(): void {
    if (this.editor && !this.disabled) {
      const code = this.editor.state.doc.toString()
      const formatted = this.formatMjmlCode(code)

      const transaction = this.editor.state.update({
        changes: { from: 0, to: this.editor.state.doc.length, insert: formatted }
      })
      this.editor.dispatch(transaction)
      this.editor.focus()
    }
  }

  private formatMjmlCode(code: string): string {
    try {
      // Use js-beautify for professional XML/HTML formatting
      // Only using valid HTMLBeautifyOptions to avoid TypeScript errors
      return beautify.html(code, {
        indent_size: 2,
        indent_char: ' ',
        max_preserve_newlines: 1,
        preserve_newlines: true,
        end_with_newline: true,
        wrap_line_length: 0,
        indent_inner_html: true,
        indent_empty_lines: false,
        // MJML/XML specific options
        wrap_attributes: 'auto',
        wrap_attributes_indent_size: 2,
        unformatted: ['mj-raw'], // Don't format content inside mj-raw tags
        content_unformatted: ['pre', 'textarea', 'mj-raw'],
        extra_liners: ['head', 'body', '/html']
      })
    } catch (error) {
      console.warn('Failed to format MJML code with js-beautify, using original:', error)
      return code
    }
  }
}
